import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { canAccessBillingSummary, shouldShowCompanySettingsEntry, shouldShowRecycleBinEntry, useAuthContext } from '@/auth';
import { useLanguage } from '@/i18n';
import { useSettings } from '@/providers/SettingsProvider';
import { KeenIcon } from '@/components';
import { MenuItem, MenuLink, MenuSub, MenuTitle, MenuSeparator, MenuArrow, MenuIcon } from '@/components/menu';
import { UserAvatar } from '@/components/ui/name-avatar';
import { useAbility } from '@/providers/';

const DropdownUser = () => {
  const { ability } = useAbility();
  const { settings, storeSettings } = useSettings();
  const { logout, auth, logoutLoading } = useAuthContext();
  const user = auth?.user;
  // const fullName = auth.user.first_name + ' ' + auth.user.last_name;
  const { isRTL } = useLanguage();

  const handleThemeMode = event => {
    const newThemeMode = event.target.checked ? 'dark' : 'light';
    storeSettings({
      themeMode: newThemeMode
    });
  };

  const handlePrivacyToggle = () => {
    storeSettings({ showPrices: !settings.showPrices });
  };
  const fullName = (auth?.user?.first_name || auth?.user?.last_name)
    ? `${auth?.user?.first_name || ''} ${auth?.user?.last_name || ''}`.trim()
    : (auth?.user?.name || 'N/A');
  const buildHeader = () => {
    return <div className="flex items-center justify-between px-5 py-1.5 gap-1.5">
      <div className="flex items-center gap-2">
        {auth.user?.profile_image ? (
          <img className="size-9 rounded-full border-2 border-success" src={auth.user.profile_image} alt="image" />
        ) : (
          <UserAvatar name={fullName} className='!size-9 !text-base' />
        )}
        {/* <img className="size-9 rounded-full border-2 border-success" src={toAbsoluteUrl('/media/avatars/300-2.png')} alt="" /> */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm text-gray-800 font-semibold leading-none">

            {fullName}
          </span>
          <a href={`mailto:${auth.user.email}`} className="text-xs text-gray-600 hover:text-primary font-medium leading-none line-clamp-1 break-all">
            {auth.user.email}
          </a>
        </div>
      </div>
    </div>;
  };
  const buildMenu = () => {
    return <Fragment>
      <MenuSeparator />
      <div className="flex flex-col">
        {ability.can('manage', 'all') ? (
          <MenuItem toggle="dropdown" trigger="hover" dropdownProps={{
            placement: isRTL() ? 'left-start' : 'right-start',
            modifiers: [{
              name: 'offset',
              options: {
                offset: isRTL() ? [50, 0] : [-50, 0] // [skid, distance]
              }
            }]
          }}>
            {/* <Can I="manage" a="all" ability={ability}> */}
            <MenuLink>
              <MenuIcon>
                <KeenIcon icon="setting-2" />
              </MenuIcon>
              <MenuTitle>
                <FormattedMessage id="USER.MENU.MY_ACCOUNT" />
              </MenuTitle>
              <MenuArrow>
                <KeenIcon icon="right" className="text-3xs rtl:transform rtl:rotate-180" />
              </MenuArrow>
            </MenuLink>
            {/* </Can> */}
            <MenuSub className="menu-default light:border-gray-300 w-[200px]] md:w-[220px]">
              <MenuItem>
                <MenuLink path="/account/home/user-profile">
                  <MenuIcon>
                    <KeenIcon icon="user-tick" />
                  </MenuIcon>
                  <MenuTitle>
                    <span className="menu-title">
                      <FormattedMessage id="USER.MENU.MY_PROFILE" />
                    </span>
                  </MenuTitle>
                </MenuLink>
              </MenuItem>
              <MenuItem>
                <MenuLink path="/account/security/overview">
                  <MenuIcon>
                    <KeenIcon icon="shield-tick" />
                  </MenuIcon>
                  <MenuTitle>
                    <span className="menu-title">
                      <FormattedMessage id="USER.MENU.SECURITY" />
                    </span>
                  </MenuTitle>
                </MenuLink>
              </MenuItem>
              {shouldShowCompanySettingsEntry(user) && (
                <MenuItem>
                  <MenuLink path="/account/global/settings">
                    <MenuIcon>
                      <KeenIcon icon="setting" />
                    </MenuIcon>
                    <MenuTitle>
                      <span className="menu-title">
                        <FormattedMessage id="USER.MENU.COMPANY_SETTINGS" />
                      </span>
                    </MenuTitle>
                  </MenuLink>
                </MenuItem>
              )}
              {canAccessBillingSummary(user) && (
                <MenuItem>
                  <MenuLink path="/billing/summary">
                    <MenuIcon>
                      <KeenIcon icon="graph-up" />
                    </MenuIcon>
                    <MenuTitle>
                      <span className="menu-title">
                        <FormattedMessage id="USER.MENU.PLAN_USAGE" defaultMessage="Plan Usage" />
                      </span>
                    </MenuTitle>
                  </MenuLink>
                </MenuItem>
              )}
              {shouldShowRecycleBinEntry(user) && (
                <>
                  {/* <MenuItem>
                    <MenuLink path="/account/global/settings">
                      <MenuIcon>
                        <KeenIcon icon="setting" />
                      </MenuIcon>
                      <MenuTitle>
                        <span className="menu-title">
                          <FormattedMessage id="USER.MENU.COMPANY_SETTINGS" />
                        </span>
                      </MenuTitle>
                    </MenuLink>
                  </MenuItem> */}
                  <MenuItem>
                    <MenuLink path="/recycle-bin">
                      <MenuIcon>
                        <KeenIcon icon="update-file" />
                      </MenuIcon>
                      <MenuTitle>
                        <span className="menu-title">
                          <FormattedMessage id="USER.MENU.RECYCLE_BIN" />
                        </span>
                      </MenuTitle>
                    </MenuLink>
                  </MenuItem>
                </>
              )}
              {/* <MenuItem>
                  <MenuLink path="/account/integrations">
                    <MenuIcon>
                      <KeenIcon icon="switch" />
                    </MenuIcon>
                    <MenuTitle>
                      <FormattedMessage id="USER.MENU.INTEGRATIONS" />
                    </MenuTitle>
                  </MenuLink>
                </MenuItem> */}
            </MenuSub>
          </MenuItem>
        ) : (
          <>
            <Link to='/account/home/user-profile' className="menu-item mb-0.5">
              <div className="menu-link">
                <span className="menu-icon">
                  <KeenIcon icon="user" />
                </span>
                <span className="menu-title">
                  <FormattedMessage id="USER.MENU.MY_PROFILE" />
                </span>
              </div>
            </Link>
            <Link to='/account/security/overview' className="menu-item mb-0.5">
              <div className="menu-link">
                <span className="menu-icon">
                  <KeenIcon icon="shield-tick" />
                </span>
                <span className="menu-title">
                  <FormattedMessage id="USER.MENU.SECURITY" />
                </span>
              </div>
            </Link>
          </>
        )}
        <MenuSeparator />
      </div>
    </Fragment>;
  };
  const buildFooter = () => {
    return <div className="flex flex-col">
      <div className="menu-item mb-0.5">
        <div className="menu-link">
          <span className="menu-icon">
            <KeenIcon icon="moon" />
          </span>
          <span className="menu-title">
            <FormattedMessage id="USER.MENU.DARK_MODE" />
          </span>
          <label className="switch switch-sm">
            <input name="theme" type="checkbox" checked={settings.themeMode === 'dark'} onChange={handleThemeMode} value="1" />
          </label>
        </div>
        <div className="menu-link">
          <span className="menu-icon">
            <KeenIcon icon="eye" />
          </span>
          <span className="menu-title">
            <FormattedMessage id="USER.MENU.PRIVACY_MODE" />
          </span>
          <label className="switch switch-sm">
            <input name="privacy" type="checkbox" checked={settings.showPrices} onChange={handlePrivacyToggle} value="1" />
          </label>
        </div>
      </div>

      <div className="menu-item px-4 py-1.5">
        <a onClick={logout} disabled={logoutLoading} className="btn btn-sm btn-light justify-center">
          {logoutLoading ? <FormattedMessage id="USER.MENU.PLEASE_WAIT" /> : <FormattedMessage id="USER.MENU.LOGOUT" />}
        </a>
      </div>
    </div>;
  };
  return <MenuSub className="menu-default light:border-gray-300 w-[200px] md:w-[250px]" rootClassName="p-0">
    {buildHeader()}
    {buildMenu()}
    {buildFooter()}
  </MenuSub>;
};

export { DropdownUser };
